import type { CollectionCardConfig } from '@/lib/blocks/collection/blocks';
import { buildCardStyle, resolveSymmetricGridLayout } from '@/lib/blocks/collection/blocks';
import {
  applyCollectionButtonSettingsToItems,
  readCollectionButtonSettings,
} from '@/lib/blocks/collection/button-settings';
import {
  getIndexCollectionVariantProps,
  readIndexCollectionDefinition,
  resolveIndexCollectionProps,
} from '@/lib/blocks/collection/index-collection/blocks';
import { readIndexCollectionLayout } from '@/lib/blocks/collection/index-collection/layout';
import type { PageBlock } from '@/types/block';
import { CollectionGridRenderer } from './collection-card-renderer';
import { IndexCollectionCard } from './index-collection/index-collection-card';
import { INDEX_COLLECTION_RENDERERS_BY_CONTENT_TYPE } from './index-collection/index-collection-renderers';

export function IndexCollectionRenderer({
  block,
  cardConfig,
}: {
  block: PageBlock;
  cardConfig: CollectionCardConfig;
}) {
  const definition = readIndexCollectionDefinition(block);
  const contentTypeEntry = definition
    ? INDEX_COLLECTION_RENDERERS_BY_CONTENT_TYPE.get(definition.contentType)
    : undefined;
  const props = resolveIndexCollectionProps(block);
  const items = applyCollectionButtonSettingsToItems(
    props.items,
    cardConfig,
    readCollectionButtonSettings(block.props as Record<string, unknown>),
  );
  const variantProps = getIndexCollectionVariantProps(props);
  const layout = readIndexCollectionLayout(block);
  const { style: cardStyle, hasCardStyling } = buildCardStyle(variantProps);
  const renderFieldForItem = contentTypeEntry?.buildRenderFieldFactory(block, cardConfig, layout);

  if (
    !definition ||
    !contentTypeEntry ||
    !renderFieldForItem ||
    cardConfig.fields.length === 0 ||
    items.length === 0
  ) {
    return null;
  }

  return (
    <CollectionGridRenderer
      items={items}
      cardConfig={cardConfig}
      layout={resolveSymmetricGridLayout(variantProps)}
      paddingClassName=""
      cardStyle={cardStyle}
      hasCardStyling={hasCardStyling}
      renderCard={(item) => (
        <IndexCollectionCard
          contentType={definition.contentType}
          block={block}
          item={item}
          cardConfig={cardConfig}
          layout={layout}
          variantProps={variantProps}
          renderField={renderFieldForItem(item)}
          hasCardStyling={hasCardStyling}
        />
      )}
    />
  );
}
